import {
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  TextField,
  Alert,
  Chip,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import CustomFormLabel from "../../src/components/forms/theme-elements/CustomFormLabel";
import CustomTextField from "../../src/components/forms/theme-elements/CustomTextField";
import AuthSocialButtons from "./AuthSocialButtons";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import axios from "axios";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../../firebaseConfig";
import { useDispatch } from 'react-redux';

import toast from "react-hot-toast";
import { setUser } from "../../src/store/apps/auth/AuthSlice";
import { loginType } from "../../src/types/auth/auth";
import { postLoginPath } from '../../src/common/post-login-redirect';
import {
  trackAuthCodeResent,
  trackAuthLoginFailed,
  trackAuthLoginSuccess,
  trackAuthOtpSubmitted,
} from "../../src/utils/analytics";


const AuthLogin = ({ title, subtitle, subtext }: loginType) => {
  const router = useRouter();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [emailSent, setEmailSent] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [errors, setErrors] = useState(''); // email input
  const [resending, setResending] = useState(false);
  const currentEnv = process.env.NEXT_PUBLIC_NODE_ENV || 'prod';
  const BASE_URL = process.env.NEXT_PUBLIC_API_URL;
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleLogin = async (email: string) => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    const requestData = {
      email,
      env: 'prod',
    };
    if (currentEnv !== 'prod') { // only localhost for now
      requestData.env = currentEnv;
    }
    try {
      await axios.post(`${BASE_URL}/email_link`, requestData, config);
      setEmailSent(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const emailRegex = /\S+@\S+\.\S+/;
    if (emailValue === '') {
      setErrors('Email no puede estar vacío');
      setLoading(false);
      return;
    }
    if (!emailRegex.test(emailValue)) {
      setErrors('Por favor ingresa un email válido');
      setLoading(false);
      return;
    }
    handleLogin(emailValue);
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData('text').slice(0, 6);
    if (/[^0-9]/.test(pasteData)) return;

    const newOtp = [...otp];
    pasteData.split('').forEach((char, index) => {
      newOtp[index] = char;
    });
    setOtp(newOtp);

    if (pasteData.length === 6) {
      otpRefs.current[5]?.blur();
    } else {
      otpRefs.current[pasteData.length]?.focus();
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    if (/[^0-9]/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    if (index === 5 && value) {
      otpRefs.current[index]?.blur();
    }
  };

  const handleOtpSubmit = async () => {
    setVerifying(true);
    trackAuthOtpSubmitted({ source: 'login' });
    const otpCode = otp.join('');
    const payload = {
      email: emailValue,
      code: otpCode,
    }
    try {
      const res = await axios.post(`${BASE_URL}/verify_code`, payload);
      if (res) {
        await signInWithCustomToken(auth, res.data.token);
        const user = auth.currentUser;
        if (user) {
          trackAuthLoginSuccess({ source: 'login' });
          dispatch(setUser(user));
          router.push(postLoginPath());
        }
      }
    } catch (error) {
      const errorString = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail || 'Error desconocido';
      trackAuthLoginFailed({ source: 'login', error_type: 'verify_failed' });
      toast.error(`Error al verificar el código - ${errorString}`, {
        duration: 5000
      });
      console.error(error);
      setOtp(Array(6).fill(''));
    } finally {
      setVerifying(false);
    }
  };

  const handleReSendCode = async () => {
    setResending(true);
    trackAuthCodeResent({ source: 'login' });
    try {
      await axios.post(`${BASE_URL}/email_link`, { email: emailValue, env: process.env.NEXT_PUBLIC_NODE_ENV || 'prod' });
    } catch (error) {
      console.error('Error al reenviar el código:', error);
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      {title ? (
        <Typography fontWeight="700" variant="h3" mb={1}>
          {title}
        </Typography>
      ) : null}

      {subtext}

      {!emailSent ? (
        <>
          <AuthSocialButtons title="Ingresar con" />
          <Box mt={3}>
            <Divider>
              <Typography
                component="span"
                color="textSecondary"
                variant="h6"
                fontWeight="400"
                position="relative"
                px={2}
              >
                o ingresar con código por email
              </Typography>
            </Divider>
          </Box>

          <Stack>
            <Box
              mb={2}
            >
              <CustomFormLabel htmlFor="email">Email</CustomFormLabel>
              <CustomTextField
                id="email"
                variant="outlined"
                fullWidth
                autoComplete="off"
                onChange={(e) => {
                  setEmailValue(e.target.value)
                  setErrors('')
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errors ? '#FA896B' : '',
                    },
                  },
                }}
              />
              {errors && <Typography color="error">{errors}</Typography>}
            </Box>
          </Stack>
          <Box
            my={2}>
            <Button
              color="primary"
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSubmit}
              type="submit"
            >
              {loading ? 'Cargando...' : 'Iniciar sesión'}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box mt={4}>
            {emailSent && (
              <>
                <Stack mb={4}>
                  <Typography color="textSecondary" textAlign="center">
                    Ingresa el código de 6 dígitos que enviamos a tu email
                  </Typography>
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Chip
                      icon={<AccessTimeIcon fontSize="small" />}
                      label="El código es válido por 15 minutos"
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="center" mb={4}>
                  {otp.map((value, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (otpRefs.current[index] = el)}
                      value={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOtpChange(e, index)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      inputProps={{
                        maxLength: 1,
                        style: {
                          textAlign: 'center',
                          height: '50px',
                          padding: 0,
                          fontSize: '1.5rem',
                          fontWeight: 500,
                        },
                      }}
                      sx={{ width: '50px', textAlign: 'center' }}
                    />
                  ))}
                </Stack>
                <Alert severity="warning">
                  {t('if_not_receive_code')}
                </Alert>
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={handleOtpSubmit}
                  disabled={verifying || resending}
                >
                  {verifying ? 'Verificando' : 'Verificar Código'}
                </Button>
              </>
            )}
          </Box>
          {emailSent ? (
            <Box 
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Button
                color="info"
                size="small"
                onClick={handleReSendCode}
                disabled={resending}
                sx={{ mt: 2 }}
              >
                {resending ? 'Reenviando...' : 'Reenviar Código'}
              </Button>
            </Box>
          ) : null}
        </>
      )}
      {!emailSent && subtitle}
    </>
  )
};

export default AuthLogin;
